import React, { useEffect, useState } from "react";

import { ViewBooking } from "collins-js-sdk";
import { CalendarDays } from "lucide-react";

import { addOrUpdateBooking } from "../../../../../APIs/Booking/UpdateBooking";
import { EDSSearch } from "../../../../../components/eds";
import { useAppContext } from "../../../../../hooks/useAppContext";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { filterBookingsForTerm } from "../../../../../utils/booking";
import { Button as EDSButton } from "../../../../ui/button";
import { DiaryBookingCard } from "../DiaryBookingCardList/DiaryBookingCard/DiaryBookingCard";

type DiarySearchProps = {
  onFocus?: any;
};

export const DiarySearch = ({ onFocus }: DiarySearchProps) => {
  const diaryContext = useDiaryContext();

  const appContext = useAppContext();

  const [searchstring, setSearchString] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [filteredBookings, setFilteredBookings] = useState<ViewBooking[]>([]);

  useEffect(() => {
    updateBookings(searchstring);
  }, [diaryContext.bookings]);

  const handleOnSearch = (criteria: string) => {
    updateBookings(criteria);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchString(value);
    updateBookings(value);
  };

  const handleFocused = (isFocused: boolean) => {
    onFocus(isFocused);
    setIsFocused(isFocused);
    updateBookings("");
  };

  const updateBookings = (term = "") => {
    setSearchString(term);
    setFilteredBookings(
      term
        ? filterBookingsForTerm(diaryContext.bookings, term)
        : (diaryContext.bookings ?? [])
    );
  };

  const handleBookingChanged = (booking: ViewBooking) => {
    addOrUpdateBooking({
      booking,
      diaryContext
    });
  };

  return (
    <>
      <div className="DiarySearch flex justify-between items-center gap-3 rounded-tl-lg max-h-[55px] max-w-[var(--sidebarWidth)] py-3 pr-1.5 pl-4 bg-[var(--panelBackground)] m-0 box-border">
        <EDSSearch
          id="diary-search-input"
          name="diary-search"
          placeholder="Search bookings by name or ID"
          value={searchstring}
          onSearch={handleOnSearch}
          onChange={handleOnChange}
          onClick={() => {
            handleFocused(true);
          }}
          className="w-full"
        />
        {isFocused && (
          <EDSButton
            variant="outline"
            size="sm"
            className="DiarySearch__SearchSubmit eds-button h-9"
            onClick={() => {
              handleFocused(false);
            }}
          >
            Cancel
          </EDSButton>
        )}
      </div>
      {isFocused && (
        <div className="DiarySearch__Results w-[var(--sidebarWidth)] absolute top-[55px] left-0 right-0 bottom-0 overflow-y-auto bg-[var(--panelBackground)] border-t border-[var(--borderColor)] z-10">
          <div className="DiarySearch__ResultsContainer p-0 h-full">
            {filteredBookings && filteredBookings.length > 0 ? (
              filteredBookings.map((booking, index) => (
                <DiaryBookingCard
                  key={booking.id || index.toString()}
                  index={index}
                  style={{}}
                  parent={{
                    props: {
                      bookings: filteredBookings,
                      venueConfig: diaryContext.venueConfig,
                      venueGroupConfig: appContext.venueGroupConfig,
                      onBookingChanged: handleBookingChanged,
                      selectedView: diaryContext.selectedView,
                      selectedBooking: diaryContext.selectedBooking,
                      setSelectedBooking: (selectedBooking: ViewBooking) => {
                        diaryContext.setDiaryContext({
                          selectedBooking,
                          selectedBookingV4: undefined
                        });
                      },
                      refreshBookings: () => {},
                      isOnRunSheet: false,
                      disableSwipe: false,
                      setDisableSwipe: () => {}
                    }
                  }}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[25vh] text-center p-8">
                <CalendarDays className="h-12 w-12 text-muted-foreground mb-6 opacity-50" />
                <h2 className="text-xl font-semibold mb-3">
                  No Bookings Found
                </h2>
                <p className="text-muted-foreground text-sm max-w-md">
                  No bookings match your search criteria.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
